export const MCC_FRAME_EXIT_BUTTON_AUTOMATION_ID = 'mcc-frame-exit-button';

export const MCC_FRAME_TOOLBAR_AUTOMATION_ID = 'mcc-frame-toolbar';
export const MCC_FRAME_TOOLBAR_SELECTOR = `[data-automation-id="${MCC_FRAME_TOOLBAR_AUTOMATION_ID}"]`;

const isElementVisible = (
  element: HTMLElement | null
): element is HTMLElement => {
  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    const {display, visibility} = window.getComputedStyle(currentElement);
    if (display === 'none' || visibility === 'hidden') return false;
    currentElement = currentElement.parentElement;
  }
  return element !== null;
};

// Toolbar actions must be keyboard-reachable: buttons that opt out of
// sequential navigation (tabIndex < 0, e.g. the click-to-edit frame width
// label) never join the Tab chain.
const isTabbable = (button: HTMLButtonElement) => button.tabIndex >= 0;

/**
 * Visible action buttons in a frame toolbar (hide/reveal, exit, …). Skips
 * buttons inside `display: none` wrappers (e.g. narrow-width breakpoints)
 * and buttons that are not tabbable (tabIndex < 0, e.g. the click-to-edit
 * frame width label). Used so Tab hops stay preventScroll without hardcoding
 * which controls exist.
 */
export const getVisibleToolbarActionButtons = (
  toolbar: HTMLElement
): HTMLButtonElement[] =>
  Array.from(toolbar.querySelectorAll<HTMLButtonElement>('button'))
    .filter(isTabbable)
    .filter(isElementVisible);

// The focused toolbar stays visible via `:focus-within` even when
// `data-revealed` is false, so its actions stay in the chain.
const isInFocusedMCCFrameToolbar = (button: HTMLButtonElement) =>
  button
    .closest(MCC_FRAME_TOOLBAR_SELECTOR)
    ?.contains(button.ownerDocument.activeElement) ?? false;

const isRevealedMCCFrameToolbarAction = (button: HTMLButtonElement) =>
  button.closest('[data-revealed="false"]') === null ||
  isInFocusedMCCFrameToolbar(button);

const getRevealedMCCFrameToolbarActions = (doc: Document) =>
  Array.from(
    doc.querySelectorAll<HTMLButtonElement>(
      `${MCC_FRAME_TOOLBAR_SELECTOR} button`
    )
  )
    .filter(isTabbable)
    .filter(isRevealedMCCFrameToolbarAction)
    .filter(isElementVisible);

/**
 * Returns the visible MCC frame toolbar action adjacent to `button` in
 * document order, or null when `button` is at the edge of the chain. The
 * chain spans every frame's toolbar when "Show all breakpoints" is on, so it
 * runs hide/reveal → exit within a frame before continuing into the next one.
 *
 * Hidden actions (on frames that are not active, selected, or hovered) are skipped.
 */
export const getAdjacentMCCFrameToolbarAction = (
  button: HTMLButtonElement,
  direction: 1 | -1
) => {
  const actions = getRevealedMCCFrameToolbarActions(button.ownerDocument);
  const currentIndex = actions.indexOf(button);
  if (currentIndex === -1) return null;
  return actions[currentIndex + direction] ?? null;
};

/**
 * Focuses the first visible MCC frame toolbar action in the document without
 * scrolling, returning whether one was found. The MCC frame tools render
 * before the canvas bar in the document, so controls that are last in the tab
 * order (e.g. the MCC toggle) use this to hand focus to the frame tools
 * instead of letting sequential navigation escape to the browser chrome.
 */
export const focusFirstMCCFrameToolbarAction = (doc: Document): boolean => {
  const action = getRevealedMCCFrameToolbarActions(doc)[0] ?? null;
  if (!action) return false;

  action.focus({preventScroll: true});
  return true;
};
